import React, { useState } from 'react';
import LogoColor from '../logo/images/logo-color.svg';
import Style from './auth.module.scss';

const EyeOpen = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
  </svg>
);
const EyeOff = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
    <line x1="1" y1="1" x2="23" y2="23"/>
  </svg>
);

export const BRAND = '#111111';
export const AMBER = '#FFB800';

// ── Decorative data (shared across all auth split-layout pages) ───────────────

const BUBBLES = [
  { s: 220, top: '-11%',   right: '-9%'  },
  { s: 230, bottom: '-10%',right: '-10%' },
  { s: 110, top: '36%',    right: '-8%'  },
  { s:  90, top: '3%',     right: '4%'   },
  { s:  58, top: '-2%',    right: '28%'  },
  { s:  42, top: '2%',     right: '53%'  },
  { s:  28, top: '-1%',    right: '73%'  },
  { s:  65, top: '18%',    right: '14%'  },
  { s:  38, top: '20%',    right: '43%'  },
  { s:  72, bottom: '8%',  right: '12%'  },
  { s:  48, top: '54%',    right: '4%'   },
  { s:  34, top: '68%',    right: '30%'  },
  { s:  24, bottom: '38%', right: '46%'  },
  { s:  20, top: '80%',    right: '16%'  },
  { s: 100, top: '-2%',    left: '0%'    },
  { s:  75, top: '26%',    left: '0%'    },
  { s:  55, top: '52%',    left: '0%'    },
  { s:  40, bottom: '10%', left: '1%'    },
  { s:  26, top: '20%',    left: '18%'   },
  { s:  20, top: '42%',    left: '9%'    },
  { s:  16, bottom: '28%', left: '12%'   },
];

const LEFT_DECO = [
  { s: 240, top: '-11%',   left: '-11%', color: BRAND, op: 0.06 },
  { s: 130, top: '2%',     left: '54%',  color: AMBER, op: 0.18 },
  { s: 170, bottom: '-10%',left: '-8%',  color: BRAND, op: 0.05 },
  { s: 100, bottom: '6%',  left: '58%',  color: AMBER, op: 0.14 },
  { s:  80, top: '42%',    left: '-6%',  color: BRAND, op: 0.08 },
  { s:  55, top: '18%',    left: '68%',  color: BRAND, op: 0.07 },
  { s:  45, top: '62%',    left: '56%',  color: AMBER, op: 0.12 },
  { s:  38, top: '32%',    left: '78%',  color: BRAND, op: 0.06 },
  { s:  30, bottom: '28%', left: '72%',  color: AMBER, op: 0.10 },
];

// ── Layout shell ──────────────────────────────────────────────────────────────

export function AuthSplitLayout({ illustration, children }) {
  return (
    <div className={Style.root} style={s.root}>

      <svg style={s.wave} viewBox="0 0 100 100" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M0,0 L50,0 Q65,25 50,50 Q35,75 50,100 L0,100 Z" fill="white" />
      </svg>

      <div className={Style.left} style={s.left}>
        {LEFT_DECO.map((d, i) => (
          <div key={i} style={{
            position: 'absolute', borderRadius: '50%', pointerEvents: 'none',
            background: `rgba(${d.color === BRAND ? '30,27,75' : '255,200,5'},${d.op})`,
            width: d.s, height: d.s,
            top: d.top, left: d.left, bottom: d.bottom,
          }} />
        ))}
        <div style={s.leftContent}>{illustration}</div>
      </div>

      <div className={Style.right} style={s.right}>
        {BUBBLES.map((b, i) => (
          <div key={i} style={{
            position: 'absolute', borderRadius: '50%', pointerEvents: 'none',
            background: 'rgba(255,255,255,0.22)',
            width: b.s, height: b.s,
            top: b.top, right: b.right, bottom: b.bottom, left: b.left,
          }} />
        ))}
        <div className={Style.formBox} style={s.formBox}>
          <div className={Style.mobileLogo}>
            <img src={LogoColor} alt="Tu Ruta Segura" />
          </div>
          {children}
        </div>
      </div>

    </div>
  );
}

// ── Form atoms ────────────────────────────────────────────────────────────────

export function AuthInput({ type = 'text', onFocus, onBlur, ...props }) {
  const [focused, setFocused] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const isPassword = type === 'password';

  const inputStyle = isPassword
    ? { ...(focused ? s.iOn : s.i), marginBottom: 0, paddingRight: '3rem' }
    : (focused ? s.iOn : s.i);

  const inputEl = (
    <input
      type={isPassword ? (showPwd ? 'text' : 'password') : type}
      style={inputStyle}
      onFocus={e => { setFocused(true); onFocus?.(e); }}
      onBlur={e => { setFocused(false); onBlur?.(e); }}
      {...props}
    />
  );

  if (!isPassword) return inputEl;

  return (
    <div style={{ position: 'relative', marginBottom: '1.1rem' }}>
      {inputEl}
      <button
        type="button"
        tabIndex={-1}
        onClick={() => setShowPwd(v => !v)}
        style={{
          position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)',
          background: 'none', border: 'none', cursor: 'pointer',
          color: BRAND, opacity: 0.45, padding: 4,
          display: 'flex', alignItems: 'center', lineHeight: 1,
        }}
      >
        {showPwd ? <EyeOff /> : <EyeOpen />}
      </button>
    </div>
  );
}

export function AuthButton({ loading, loadingText = '…', children, type = 'submit', style: styleProp, ...props }) {
  return (
    <button
      type={type}
      disabled={loading}
      style={{ ...s.btn, ...(loading && { opacity: 0.6, cursor: 'not-allowed' }), ...styleProp }}
      {...props}
    >
      {loading ? loadingText : children}
    </button>
  );
}

export function AuthBadge({ children = 'Panel Administrativo' }) {
  return <span style={s.badge}>{children}</span>;
}

export function AuthTitle({ children, style: styleProp }) {
  return <h1 style={{ ...s.title, ...styleProp }}>{children}</h1>;
}

export function AuthLabel({ children }) {
  return <label style={s.label}>{children}</label>;
}

export function AuthError({ children }) {
  return <p style={s.err}>{children}</p>;
}

export function AuthDesc({ children }) {
  return <p style={s.desc}>{children}</p>;
}

export function AuthBackLink({ href, children }) {
  return (
    <div style={{ textAlign: 'center', marginTop: '1.2rem' }}>
      <a href={href} style={s.backLink}>{children}</a>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const iBase = {
  display: 'block', width: '100%', boxSizing: 'border-box',
  background: 'white', border: 'none', borderRadius: 10,
  padding: '0.78rem 1rem', fontSize: '0.88rem',
  color: '#1e1b4b', WebkitTextFillColor: '#1e1b4b', colorScheme: 'light',
  outline: 'none', marginBottom: '1.1rem',
  boxShadow: '0 2px 8px rgba(0,0,0,0.08)', transition: 'box-shadow 0.18s',
};

const s = {
  root: {
    minHeight: '100vh', display: 'flex',
    background: 'linear-gradient(150deg, #FFD84D 0%, #F0B300 100%)',
    position: 'relative', fontFamily: "'Inter', sans-serif",
  },
  wave: {
    position: 'absolute', top: 0, left: 0,
    width: '100%', height: '100%', zIndex: 1, pointerEvents: 'none',
  },
  left: {
    flex: '0 0 50%', position: 'relative', zIndex: 2,
    display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
  },
  leftContent: {
    position: 'relative', zIndex: 2,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    width: '100%',
    padding: 'clamp(4rem,8vh,6rem) clamp(2rem,4vw,3.5rem) clamp(2rem,4vh,3rem)',
  },
  logoCorner: {
    position: 'absolute', top: '2rem', left: '-3rem',
    height: 'clamp(70px,11vh,100px)', width: 'auto', zIndex: 3,
  },
  right: {
    flex: '0 0 50%', position: 'relative', zIndex: 2, overflow: 'hidden',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: 'clamp(2rem,5vw,4rem)',
  },
  formBox:  { width: '100%', maxWidth: 340 },
  badge: {
    display: 'inline-block', marginBottom: '0.75rem',
    padding: '0.25rem 0.75rem', borderRadius: 99,
    background: 'rgba(0,0,0,0.10)', fontSize: '0.68rem',
    fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: BRAND,
  },
  title: {
    margin: '0 0 1rem', fontWeight: 700,
    fontSize: 'clamp(1.6rem,2.3vw,2rem)', color: BRAND,
    letterSpacing: '-0.03em', lineHeight: 1.1,
  },
  label: {
    display: 'block', marginBottom: '0.3rem',
    fontWeight: 600, fontSize: '0.82rem', color: BRAND,
  },
  i:   { ...iBase },
  iOn: { ...iBase, boxShadow: `0 2px 12px rgba(0,0,0,0.12), 0 0 0 2px ${BRAND}` },
  err: { color: '#7f1d1d', fontSize: '0.8rem', fontWeight: 700, margin: '0 0 0.75rem', letterSpacing: '-0.01em' },
  desc: { margin: '0 0 1.4rem', fontSize: '0.84rem', color: BRAND, opacity: 0.65, lineHeight: 1.5 },
  btn: {
    width: '100%', padding: '0.9rem', borderRadius: 13,
    border: 'none', background: BRAND, color: 'white',
    fontWeight: 800, fontSize: '0.95rem', cursor: 'pointer',
    letterSpacing: '-0.01em', boxShadow: '0 6px 20px rgba(30,27,75,0.30)',
  },
  backLink: { fontSize: '0.74rem', fontWeight: 600, color: BRAND, opacity: 0.65, textDecoration: 'none' },
};
